import { createFileRoute } from '@tanstack/react-router';
import { useSuspenseQuery } from '@apollo/client';
import {
  Alert,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { creatorHasCampaignDocument } from '@social-garden/api/documents/creatorHasCampaign.ts';
import { useCallback, useMemo } from 'react';
import {
  assertUnreachable,
  formatCents,
  getLatestPreviewRevision,
  getUriWithHash,
} from '@social-garden/utils/helpers.ts';
import { useTranslation } from 'react-i18next';
import {
  getContentTypeFromCampaignType,
  getContentTypeNameFromCampaignType,
  getMediaTypeFromContentType,
} from '@social-garden/utils/content.ts';
import { faPhotoFilmMusic } from '@fortawesome/pro-thin-svg-icons/faPhotoFilmMusic';
import { CreatorHasCampaignStatus } from '@social-garden/api/gql/graphql.ts';
import ReplaceUrlWithLink from '@/components/ReplaceUrlWithLink.tsx';
import useCreatorHasCampaignStatus from '@/hooks/useCreatorHasCampaignStatus.ts';
import DeliveryAddress from '@/components/DeliveryAddress.tsx';
import PreviewRevisionCard from '@/components/PreviewRevisionCard.tsx';
import EmptyState from '@/components/EmptyState.tsx';
import ChannelTypeChip from '@/components/ChannelTypeChip.tsx';
import ChannelContentTypeChip from '@/components/ChannelContentTypeChip.tsx';
import CollaborationDetailsHeader from '@/components/CollaborationDetails/CollaborationDetailsHeader.tsx';
import CollaborationDetailsActionButton from '@/components/CollaborationDetails/CollaborationDetailsActionButton.tsx';
import CollaborationDetailsContent from '@/components/CollaborationDetails/CollaborationDetailsContent.tsx';
import { CURRENCY } from '@/constants/Currency.ts';

export const Route = createFileRoute('/_manager/_app/collaborations/$id/')({
  component: CollaborationDetails,
});

function CollaborationDetails() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation(['common', 'manager']);

  const {
    data: { creatorHasCampaign },
  } = useSuspenseQuery(creatorHasCampaignDocument, {
    variables: {
      id: id,
    },
  });

  const { creatorHasCampaignStatusNames, creatorHasCampaignStatusColors } =
    useCreatorHasCampaignStatus();

  const statusInfoAlert = useMemo(() => {
    switch (creatorHasCampaign.status) {
      case CreatorHasCampaignStatus.APPLIED:
        return (
          <Alert severity="info">
            {t('manager:creatorHasCampaign.statusInfo.applied')}
          </Alert>
        );
      case CreatorHasCampaignStatus.APPLICATION_DECLINED:
        return (
          <Alert severity="info">
            {t('manager:creatorHasCampaign.statusInfo.applicationDeclined')}
          </Alert>
        );
      case CreatorHasCampaignStatus.PREVIEW_PENDING:
        return (
          <Alert severity="info">
            {t('manager:creatorHasCampaign.statusInfo.previewPending')}
          </Alert>
        );
      case CreatorHasCampaignStatus.PREVIEW_SUBMITTED:
        return (
          <Alert severity="info">
            {t('manager:creatorHasCampaign.statusInfo.previewSubmitted')}
          </Alert>
        );
      case CreatorHasCampaignStatus.CONTENT_PENDING:
        return (
          <Alert severity="info">
            {t('manager:creatorHasCampaign.statusInfo.contentPending')}
          </Alert>
        );
      case CreatorHasCampaignStatus.CONTENT_SUBMITTED:
        return (
          <Alert severity="success">
            {t('manager:creatorHasCampaign.statusInfo.contentSubmitted')}
          </Alert>
        );
      case CreatorHasCampaignStatus.REPORT_FAILED:
        return (
          <Alert severity="warning">
            {t('manager:creatorHasCampaign.statusInfo.reportFailed')}
          </Alert>
        );
      case CreatorHasCampaignStatus.REPORT_COMPLETED:
        return (
          <Alert severity="success">
            {t('manager:creatorHasCampaign.statusInfo.reportCompleted')}
          </Alert>
        );
      case CreatorHasCampaignStatus.DISCONTINUED:
        return (
          <Alert severity="info">
            {t('manager:creatorHasCampaign.statusInfo.discontinued')}
          </Alert>
        );
      case CreatorHasCampaignStatus.CAMPAIGN_SUSPENDED:
        return null;
      default:
        assertUnreachable(creatorHasCampaign.status);
    }
  }, [creatorHasCampaign.status, t]);

  const latestPreviewRevision = useMemo(
    () => getLatestPreviewRevision(creatorHasCampaign.previewRevisions),
    [creatorHasCampaign.previewRevisions],
  );

  const mediaType = useMemo(
    () =>
      getMediaTypeFromContentType(
        getContentTypeNameFromCampaignType(
          creatorHasCampaign.campaign.typeable,
        ),
      ),
    [creatorHasCampaign.campaign.typeable],
  );

  const handleOnNavigateToCampaign = useCallback(async () => {
    await navigate({
      to: '/campaigns/$id',
      params: {
        id: creatorHasCampaign.campaign.id,
      },
    });
  }, [navigate, creatorHasCampaign.campaign.id]);

  return (
    <Container>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Card variant="outlined">
            <CardMedia
              sx={{
                aspectRatio: 21 / 9,
                width: '100%',
              }}
              image={getUriWithHash(creatorHasCampaign.campaign.headerImage)}
              title={creatorHasCampaign.campaign.name}
              draggable={false}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <CollaborationDetailsHeader
                    creatorHasCampaign={creatorHasCampaign}
                    actionButton={
                      <CollaborationDetailsActionButton
                        creatorHasCampaign={creatorHasCampaign}
                      />
                    }
                  />
                </Grid>
                {creatorHasCampaign.creator.description ? (
                  <Grid
                    size={{
                      xs: 12,
                      md: 10,
                      lg: 8,
                      xl: 6,
                    }}>
                    <Typography
                      sx={{
                        whiteSpace: 'pre-line',
                      }}>
                      <ReplaceUrlWithLink>
                        {creatorHasCampaign.creator.description}
                      </ReplaceUrlWithLink>
                    </Typography>
                  </Grid>
                ) : null}
                <Grid container size={12} spacing={1}>
                  <Grid size="auto">
                    <Chip
                      label={
                        creatorHasCampaignStatusNames[creatorHasCampaign.status]
                      }
                      variant="filled"
                      color={
                        creatorHasCampaignStatusColors[
                          creatorHasCampaign.status
                        ]
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid size="auto">
                    <ChannelTypeChip
                      channelType={
                        creatorHasCampaign.campaign.typeable.channelType
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid size="auto">
                    <ChannelContentTypeChip
                      channelType={
                        creatorHasCampaign.campaign.typeable.channelType
                      }
                      channelContentType={getContentTypeFromCampaignType(
                        creatorHasCampaign.campaign.typeable,
                      )}
                      size="small"
                    />
                  </Grid>
                  <Grid size="auto">
                    <Chip
                      label={`${t('common:campaign.reward')}: ${formatCents(creatorHasCampaign.rewardValue, CURRENCY, resolvedLanguage)}`}
                      variant="filled"
                      size="small"
                    />
                  </Grid>
                  <Grid size="auto">
                    <Chip
                      sx={{
                        textDecoration: 'underline',
                      }}
                      label={`${t('manager:creatorHasCampaign.campaign.name.label')}: ${creatorHasCampaign.campaign.name}`}
                      variant="filled"
                      size="small"
                      clickable
                      onClick={handleOnNavigateToCampaign}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {statusInfoAlert ? <Grid size={12}>{statusInfoAlert}</Grid> : null}
        {creatorHasCampaign.status === CreatorHasCampaignStatus.DISCONTINUED &&
        creatorHasCampaign.discontinuedReason ? (
          <Grid size={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" component="div">
                  {t('common:creatorHasCampaign.discontinuedReason')}
                </Typography>
                <Typography>{creatorHasCampaign.discontinuedReason}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ) : null}
        {creatorHasCampaign.answers.length > 0 ? (
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" component="div">
                  {t('manager:creatorHasCampaign.questionsAndAnswers.header')}
                </Typography>
                <Stack spacing={2}>
                  {creatorHasCampaign.answers.map((answer, index) => (
                    <Stack key={answer.id}>
                      <Typography variant="subtitle2">
                        {`${index + 1}. ${answer.question.question}`}
                      </Typography>
                      <Typography>{answer.answer}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ) : null}
        {creatorHasCampaign.campaignGiftCodes.length > 0 ? (
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" component="div">
                  {t('manager:creatorHasCampaign.giftCode.header')}
                </Typography>
                <Stack spacing={2}>
                  {creatorHasCampaign.campaignGiftCodes.map((giftCode) => (
                    <Stack key={giftCode.id}>
                      <Typography variant="subtitle2">
                        {giftCode.gift.name}
                      </Typography>
                      <Typography>{giftCode.code}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ) : null}
        {creatorHasCampaign.deliveryAddress ? (
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" component="div">
                  {t('manager:creatorHasCampaign.deliveryAddress.header')}
                </Typography>
                <DeliveryAddress
                  address={creatorHasCampaign.deliveryAddress}
                  showApproximateAddress={
                    creatorHasCampaign.status ===
                      CreatorHasCampaignStatus.APPLIED ||
                    creatorHasCampaign.status ===
                      CreatorHasCampaignStatus.APPLICATION_DECLINED
                  }
                />
              </CardContent>
            </Card>
          </Grid>
        ) : null}
        <Grid container size={12} spacing={0}>
          <Grid size={12}>
            <Typography variant="overline" fontSize={20}>
              {t('manager:creatorHasCampaign.contentPreview.header')}
            </Typography>
          </Grid>
          <Grid size={12}>
            {latestPreviewRevision ? (
              <PreviewRevisionCard
                variant="outlined"
                previewRevision={latestPreviewRevision}
                mediaType={mediaType}
              />
            ) : (
              <Card variant="outlined">
                <CardContent>
                  <EmptyState
                    icon={faPhotoFilmMusic}
                    text={t('manager:creatorHasCampaign.contentPreview.empty')}
                  />
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
        <Grid container size={12} spacing={0}>
          <Grid size={12}>
            <Typography variant="overline" fontSize={20}>
              {t('manager:creatorHasCampaign.content.header')}
            </Typography>
          </Grid>
          <Grid size={12}>
            {creatorHasCampaign.contentUrl ? (
              <CollaborationDetailsContent
                contentUrl={creatorHasCampaign.contentUrl}
                channelType={creatorHasCampaign.campaign.typeable.channelType}
              />
            ) : (
              <Card variant="outlined">
                <CardContent>
                  <EmptyState
                    icon={faPhotoFilmMusic}
                    text={t('manager:creatorHasCampaign.content.empty')}
                  />
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
}
